// Genera un PDF con encabezado institucional FOCA (logo + subtítulo) y pie
// con firma del evaluador. Sanitiza todos los inputs.

export function sanitizeForPdf(v: unknown, maxLen = 400): string {
  if (v == null) return "";
  return String(v).replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "").slice(0, maxLen);
}

export type PdfField = { label: string; value: unknown };
export type PdfSection = { title: string; fields: PdfField[] };

export type PdfSignature = {
  evaluadorNombre?: string;
  evaluadorUsuario?: string;
  fechaRegistro?: string;
  decision?: string;
};

export type PdfDocumentSpec = {
  title: string;
  subtitle?: string;
  filename: string;
  sections: PdfSection[];
  footer?: string;
  signature?: PdfSignature;
};

let logoCache: string | null = null;

async function loadLogoDataUrl(): Promise<string | null> {
  if (logoCache !== null) return logoCache;
  try {
    const res = await fetch("/images/logo-institucional.png");
    if (!res.ok) return (logoCache = "");
    const blob = await res.blob();
    return await new Promise<string>((resolve) => {
      const fr = new FileReader();
      fr.onload = () => { logoCache = String(fr.result || ""); resolve(logoCache); };
      fr.onerror = () => { logoCache = ""; resolve(""); };
      fr.readAsDataURL(blob);
    });
  } catch { return (logoCache = ""); }
}

export async function generatePdf(spec: PdfDocumentSpec): Promise<void> {
  const { default: jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;

  // ===== Header institucional =====
  const logo = await loadLogoDataUrl();
  if (logo) {
    try { doc.addImage(logo, "PNG", marginX, 30, 90, 30, undefined, "FAST"); } catch { /* logo opcional */ }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(11, 61, 110);
  doc.text("FUNDACIÓN OFTALMOLÓGICA DEL CARIBE", pageWidth - marginX, 42, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(107, 124, 147);
  doc.text("Sistema de Valoración Preoperatoria", pageWidth - marginX, 56, { align: "right" });

  // Línea divisoria
  doc.setDrawColor(220, 228, 240);
  doc.line(marginX, 72, pageWidth - marginX, 72);

  // ===== Título del documento =====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(11, 61, 110);
  doc.text(sanitizeForPdf(spec.title), marginX, 96);

  if (spec.subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(107, 124, 147);
    doc.text(sanitizeForPdf(spec.subtitle), marginX, 112);
  }

  let cursorY = spec.subtitle ? 130 : 116;

  // ===== Secciones =====
  for (const section of spec.sections) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(11, 61, 110);
    doc.text(sanitizeForPdf(section.title), marginX, cursorY);
    cursorY += 4;

    autoTable(doc, {
      startY: cursorY + 4,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 9, cellPadding: 4, textColor: [26, 43, 69] },
      headStyles: { fillColor: [244, 247, 251], textColor: [11, 61, 110], fontStyle: "bold" },
      alternateRowStyles: { fillColor: [250, 251, 253] },
      columnStyles: { 0: { cellWidth: 160, fontStyle: "bold" }, 1: { cellWidth: "auto" } },
      body: section.fields.map((f) => [sanitizeForPdf(f.label), sanitizeForPdf(f.value, 800)]),
    });

    cursorY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 16;

    if (cursorY > pageHeight - 120) {
      doc.addPage();
      cursorY = 60;
    }
  }

  // ===== Firma del evaluador =====
  if (spec.signature) {
    if (cursorY > pageHeight - 130) { doc.addPage(); cursorY = 60; }
    const boxY = Math.max(cursorY + 20, pageHeight - 160);

    doc.setDrawColor(180, 195, 215);
    doc.setLineWidth(0.5);
    doc.rect(marginX, boxY, pageWidth - 2 * marginX, 90);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(11, 61, 110);
    doc.text("EVALUADOR RESPONSABLE", marginX + 12, boxY + 20);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(26, 43, 69);
    let y = boxY + 40;
    doc.text(`Nombre:  ${sanitizeForPdf(spec.signature.evaluadorNombre || "—")}`, marginX + 12, y); y += 14;
    doc.text(`Usuario: ${sanitizeForPdf(spec.signature.evaluadorUsuario || "—")}`, marginX + 12, y); y += 14;
    doc.text(`Fecha registro: ${sanitizeForPdf(spec.signature.fechaRegistro || "—")}`, marginX + 12, y); y += 14;

    if (spec.signature.decision) {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(11, 61, 110);
      doc.text(`Decisión final: ${sanitizeForPdf(spec.signature.decision)}`, marginX + 12, y);
    }
  }

  // ===== Footer con paginación =====
  const footerText = spec.footer || "FOCA · Sistema de Valoración Preoperatoria";
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(150, 160, 175);
    const text = `${sanitizeForPdf(footerText)}  ·  Página ${i}/${pageCount}  ·  Generado ${new Date().toLocaleString("es-CO")}`;
    doc.text(text, pageWidth / 2, pageHeight - 20, { align: "center" });
  }

  doc.save(spec.filename.endsWith(".pdf") ? spec.filename : `${spec.filename}.pdf`);
}
